import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '@/store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Users, Wifi, Coins, Edit2, Trash2, RefreshCw, ChevronLeft, ChevronRight, Calendar, Shield, User, Gift, Copy, Check, Filter, Search, X, MoreVertical, AlertTriangle, TestTube, Smartphone, Ban, ShieldCheck, MessageCircle, Bell, Send } from 'lucide-react';
import { api } from '@/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Receipt Modal Component
function ReceiptModal({ open, onClose, receipt }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    if (receipt?.text) {
      await navigator.clipboard.writeText(receipt.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Comprovante copiado!');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-card border-border mx-4 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            {receipt?.action === 'trial' ? (
              <>
                <Gift className="w-5 h-5 text-primary" />
                Teste Liberado!
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5 text-primary" />
                Renovação Realizada!
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Copie o comprovante abaixo para enviar ao cliente
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-muted/30 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap text-foreground border border-border">
            {receipt?.text}
          </div>
          <Button
            onClick={handleCopy}
            className="w-full bg-primary text-primary-foreground"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar Comprovante
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ResellersPage() {
  const { user, checkAuth } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewNotify, setRenewNotify] = useState(true);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(null); // 'resellers' ou 'tests'
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', max_connections: 1, role: 'reseller', credits: 0, custom_renewal_price: '' });
  const [editData, setEditData] = useState({ max_connections: 1, active: true, role: 'reseller', custom_renewal_price: '', created_by_override: '' });
  const [uplineSearch, setUplineSearch] = useState('');
  const [cobrancaOpen, setCobrancaOpen] = useState(false);
  const [cobrancaCfg, setCobrancaCfg] = useState(null);
  const [cobrancaSaving, setCobrancaSaving] = useState(false);
  const [cobrandoId, setCobrandoId] = useState(null);
  const [msgEdit, setMsgEdit] = useState(null); // {k, l} da mensagem sendo editada
  const [minhasConexoes, setMinhasConexoes] = useState([]);
  const [filterUpline, setFilterUpline] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState(10);
  const [actionLoading, setActionLoading] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('mine');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Block/Unblock
  const [blockOpen, setBlockOpen] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const limit = 10;

  const isAdmin = user?.role === 'admin';
  const [slotUser, setSlotUser] = useState(null);
  const [slotQty, setSlotQty] = useState(1);
  const [slotDays, setSlotDays] = useState(30);
  const [slotSaving, setSlotSaving] = useState(false);
  const enviarCobranca = async (u) => {
    if (!window.confirm(`Enviar cobrança para ${u.username} no número ${u.contato_phone}?`)) return;
    setCobrandoId(u.id);
    try {
      const r = await api.post(`/master/resellers/${u.id}/enviar-cobranca`);
      toast.success(r.data.resultado || 'Cobrança enviada!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setCobrandoId(null); }
  };
  const abrirCobrancaConfig = async () => {
    setCobrancaOpen(true);
    try { const r = await api.get('/reseller/renewal-config'); setCobrancaCfg(r.data); }
    catch { toast.error('Erro ao carregar'); }
  };
  const uploadCobrancaImg = async (msgKey, file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('nexus-token');
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing-image/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (res.ok && data.url) {
        const imgKey = msgKey.replace('msg_', 'img_');
        setCobrancaCfg((c) => ({ ...c, [imgKey]: data.url }));
        toast.success('Imagem enviada!');
      } else toast.error(data.detail || 'Erro no upload');
    } catch { toast.error('Erro no upload'); }
  };
  const salvarCobrancaConfig = async () => {
    setCobrancaSaving(true);
    try { await api.put('/reseller/renewal-config', cobrancaCfg); toast.success('Configuração salva!'); setCobrancaOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setCobrancaSaving(false); }
  };
  const salvarSlots = async (qtd) => {
    if (!slotUser) return;
    setSlotSaving(true);
    try { await api.post(`/admin/users/${slotUser.id}/connection-slots`, { quantidade: qtd, dias: slotDays }); setSlotUser(null); fetchUsers(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro ao ajustar slots'); }
    finally { setSlotSaving(false); }
  };
  const isMaster = user?.role === 'master';
  const isSimple = user?.role === 'simple';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const uplineParam = filterUpline ? `&upline_filter=${encodeURIComponent(filterUpline)}` : '';
      const endpoint = isAdmin 
        ? `/admin/all-users?page=${page}&limit=${limit}&owner_filter=${ownerFilter}${searchParam}${uplineParam}` 
        : `/master/resellers?page=${page}&limit=${limit}${searchParam}`;
      const response = await api.get(endpoint);
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      toast.error('Erro ao carregar revendedores');
    } finally {
      setLoading(false);
    }
  }, [page, isAdmin, ownerFilter, searchTerm, filterUpline]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    api.get('/connections').then((r) => setMinhasConexoes((r.data.connections || r.data || []).filter((c) => c.status?.connected || c.status === 'connected'))).catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [ownerFilter, searchTerm, filterUpline]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  // Contar expirados para os botões de ação em massa
  const expiredResellers = users.filter(u => {
    if (u.role !== 'reseller' || !u.expires_at) return false;
    return new Date(u.expires_at) < new Date();
  }).length;

  const expiredTests = users.filter(u => {
    if (!u.is_test || !u.expires_at) return false;
    return new Date(u.expires_at) < new Date();
  }).length;

  const handleBulkDelete = async () => {
    setActionLoading(true);
    try {
      const endpoint = bulkDeleteType === 'resellers' 
        ? '/admin/users/bulk/expired-resellers'
        : '/admin/users/bulk/expired-tests';
      
      const res = await api.delete(endpoint);
      const data = res.data;
      
      toast.success(`${data.deleted_count} ${bulkDeleteType === 'resellers' ? 'revendedores' : 'testes'} deletados!`);
      if (data.deleted_instances > 0) {
        toast.info(`${data.deleted_instances} instâncias WhatsApp removidas`);
      }
      
      setBulkDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar');
    } finally {
      setActionLoading(false);
    }
  };

  const openBulkDeleteDialog = (type) => {
    setBulkDeleteType(type);
    setBulkDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    // Calcular créditos necessários: 1 para criar + créditos para transferir
    const creditsToGive = newUser.credits || 0;
    const totalCreditsNeeded = 1 + creditsToGive;
    
    if (isMaster && (user?.credits || 0) < totalCreditsNeeded) {
      toast.error(`Você precisa de ${totalCreditsNeeded} créditos (1 para criar + ${creditsToGive} para transferir). Você tem ${user?.credits || 0}.`);
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = isAdmin ? '/admin/users' : '/master/resellers';
      const payload = isAdmin ? {
        username: newUser.username.trim(),
        password: newUser.password,
        max_connections: newUser.max_connections,
        role: newUser.role || 'reseller',
        credits: creditsToGive,
        custom_renewal_price: newUser.custom_renewal_price ? parseFloat(newUser.custom_renewal_price) : null
      } : {
        username: newUser.username.trim(),
        password: newUser.password,
        max_connections: newUser.max_connections,
        role: newUser.role || 'reseller',
        credits: creditsToGive,
        custom_renewal_price: newUser.custom_renewal_price ? parseFloat(newUser.custom_renewal_price) : null
      };
      
      await api.post(endpoint, payload);
      const roleLabel = newUser.role === 'master' ? 'Master' : newUser.role === 'simple' ? 'Nexu Simple' : 'Revendedor';
      toast.success(`${roleLabel} criado!`);
      setCreateDialogOpen(false);
      setNewUser({ username: '', password: '', max_connections: 1, role: 'reseller', credits: 0, custom_renewal_price: '' });
      fetchUsers();
      if (isMaster) await checkAuth();
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Erro ao criar');
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const endpoint = isAdmin ? `/admin/users/${selectedUser.id}` : `/master/resellers/${selectedUser.id}`;
      const payload = {};
      if (editData.max_connections !== selectedUser.max_connections) payload.max_connections = editData.max_connections;
      if (editData.active !== selectedUser.active) payload.active = editData.active;
      if (editData.role !== selectedUser.role) payload.role = editData.role;
      if ((editData.billing_connection_id || '') !== (selectedUser.billing_connection_id || '')) payload.billing_connection_id = editData.billing_connection_id || '';
      if (isAdmin && editData.expires_at_date && editData.expires_at_date !== (selectedUser.expires_at || '').slice(0, 10)) payload.expires_at = editData.expires_at_date + 'T23:59:59';
      // Custom renewal price: sempre enviar
      const crpVal = String(editData.custom_renewal_price || '').trim();
      const crpNum = crpVal ? parseFloat(crpVal) : 0;
      payload.custom_renewal_price = crpNum;
      
      // Upline override
      if (editData.created_by_override) {
        payload.created_by_override = editData.created_by_override;
      }
      
      await api.put(endpoint, payload);
      toast.success('Revendedor atualizado');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Erro ao atualizar');
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!blockOpen) return;
    setBlocking(true);
    try {
      const r = await api.post(`/admin/users/${blockOpen.id}/block`, { reason: blockReason });
      toast.success(r.data.message);
      setBlockOpen(null); setBlockReason('');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBlocking(false); }
  };

  const handleUnblock = async (u) => {
    if (!confirm(`Desbloquear ${u.username} e todos abaixo?`)) return;
    try {
      const r = await api.post(`/admin/users/${u.id}/unblock`);
      toast.success(r.data.message);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };



  const handleRenew = async () => {
    if (!selectedUser) return;

    if (isMaster && (user?.credits || 0) < 1) {
      toast.error('Você não tem créditos suficientes para renovar');
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = isAdmin ? `/admin/users/${selectedUser.id}/renew` : `/master/resellers/${selectedUser.id}/renew`;
      const response = await api.post(endpoint, { notify: renewNotify && !!selectedUser?.contato_phone });
      
      setRenewDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      if (isMaster) await checkAuth();
      
      // Show receipt modal
      if (response.data.receipt) {
        setCurrentReceipt(response.data.receipt);
        setReceiptModalOpen(true);
      } else {
        toast.success('Renovado por mais 1 mês!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao renovar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrial = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      // Usar o novo endpoint que pega o tempo do link de convite
      const response = await api.post(`/users/${selectedUser.id}/activate-trial`);
      
      setTrialDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      
      toast.success(response.data.message || 'Teste liberado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao liberar teste');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      if (isAdmin) {
        await api.post(`/admin/users/${selectedUser.id}/add-credits?amount=${creditsToAdd}`);
      } else {
        await api.post(`/master/resellers/${selectedUser.id}/transfer-credits`, { amount: creditsToAdd });
      }
      toast.success(`${creditsToAdd} crédito(s) transferido(s)!`);
      setAddCreditsDialogOpen(false);
      setSelectedUser(null);
      setCreditsToAdd(1);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir créditos');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const endpoint = isAdmin ? `/admin/users/${selectedUser.id}` : `/master/resellers/${selectedUser.id}`;
      await api.delete(endpoint);
      toast.success('Revendedor deletado');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (u) => {
    setSelectedUser(u);
    setEditData({ max_connections: u.max_connections, active: u.active, role: u.role, custom_renewal_price: u.custom_renewal_price || '', billing_connection_id: u.billing_connection_id || '', expires_at_date: u.expires_at ? u.expires_at.slice(0, 10) : '', created_by_override: '' });
    setUplineSearch(u.creator_username || '');
    setEditDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      master: { class: 'status-active', label: 'Master', icon: Shield },
      reseller: { class: 'status-pending', label: 'Revendedor', icon: User },
      simple: { class: 'bg-purple-500/15 text-purple-400 border-purple-500/20', label: 'Nexu Simple', icon: Smartphone }
    };
    const c = config[role] || config.reseller;
    const Icon = c.icon;
    return (
      <Badge variant="outline" className={`${c.class} text-[10px] uppercase tracking-wider gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const canHaveTrial = (u) => {
    return !u.had_trial && !u.active && !u.expires_at;
  };

  return (
    <div data-testid="resellers-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">Revendedores</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {total} revendedores • 
            {isMaster && (
              <span className="text-primary font-medium ml-1">{user?.credits || 0} créditos disponíveis</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Admin Filter */}
          {isAdmin && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[180px] bg-muted/50 border-border">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos do Sistema</SelectItem>
                <SelectItem value="mine">Criados por Mim</SelectItem>
              </SelectContent>
            </Select>
          )}
          {/* Filtro por Upline - admin + todos */}
          {isAdmin && ownerFilter === 'all' && (
            <Input
              value={filterUpline}
              onChange={(e) => setFilterUpline(e.target.value)}
              placeholder="Filtrar por upline..."
              className="w-[160px] h-9 bg-muted/50 border-border text-sm"
            />
          )}
          
          {/* Menu de Ações em Massa - Apenas Admin */}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-border">
                  <MoreVertical className="w-4 h-4 mr-2" />
                  Ações
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => openBulkDeleteDialog('resellers')}
                  className="text-destructive focus:text-destructive"
                  disabled={stats.expired === 0}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Apagar Expirados ({stats.expired})
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => openBulkDeleteDialog('tests')}
                  className="text-orange-500 focus:text-orange-500"
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  Apagar Testes Expirados
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <Button variant="outline" onClick={abrirCobrancaConfig}>
            <Bell className="w-4 h-4 mr-2" /> Cobrança/Avisos
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow">
                <Plus className="w-4 h-4 mr-2" />
                Novo Revendedor
                {isMaster && <span className="ml-1 text-xs opacity-75">(1 crédito)</span>}
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por nome de usuário..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 pr-10 bg-muted/50"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button type="submit" variant="secondary">
          <Search className="w-4 h-4 mr-2" />
          Buscar
        </Button>
      </form>

      {searchTerm && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Resultados para: <strong className="text-foreground">&quot;{searchTerm}&quot;</strong></span>
          <Button variant="ghost" size="sm" onClick={clearSearch}>
            Limpar
          </Button>
        </div>
      )}

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="glass-card border-border mx-4 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {newUser.role === 'master' ? 'Novo Master' : newUser.role === 'simple' ? 'Novo Nexu Simple' : 'Novo Revendedor'}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {isMaster 
                  ? `Criar usuário custará 1 crédito. Você tem ${user?.credits || 0} créditos.`
                  : 'Adicione um novo usuário ao sistema (créditos ilimitados)'
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-foreground">Usuário</Label>
                <Input
                  placeholder="Nome de usuário"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="bg-muted/50 border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Senha</Label>
                <Input
                  type="password"
                  placeholder="Senha"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="bg-muted/50 border-border text-foreground"
                />
              </div>
              {/* Máximo de Conexões - REMOVIDO: Todos os usuários têm 1 conexão agora */}
              {/* Tipo de usuário - disponível para Admin e Master */}
              {(isAdmin || isMaster) && (
                <div className="space-y-2">
                  <Label className="text-foreground">Tipo</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v, max_connections: 1 })}>
                    <SelectTrigger className="bg-muted/50 border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reseller">Revendedor (1 conexão)</SelectItem>
                      <SelectItem value="master">Master (1 conexão)</SelectItem>
                      <SelectItem value="simple">Nexu Simple (0 conexões)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Créditos iniciais - só para Master criando outro Master */}
              {newUser.role === 'master' && (
                <div className="space-y-2">
                  <Label className="text-foreground">Créditos Iniciais</Label>
                  <Input
                    type="number"
                    min={0}
                    max={isMaster ? (user?.credits || 0) : 999999}
                    value={newUser.credits}
                    onChange={(e) => setNewUser({ ...newUser, credits: parseInt(e.target.value) || 0 })}
                    className="bg-muted/50 border-border text-foreground"
                  />
                  {isMaster && (
                    <p className="text-xs text-muted-foreground">
                      Máximo: {user?.credits || 0} (seus créditos serão transferidos)
                    </p>
                  )}
                </div>
              )}
              {/* Valor personalizado de renovação */}
              <div className="space-y-2">
                <Label className="text-foreground">Valor Personalizado (R$)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={newUser.custom_renewal_price}
                  onChange={(e) => setNewUser({ ...newUser, custom_renewal_price: e.target.value })}
                  placeholder="Deixe vazio para usar o valor do plano"
                  className="bg-muted/50 border-border text-foreground"
                />
                <p className="text-xs text-muted-foreground">
                  Sobrescreve o valor padrao do plano na renovacao deste painel
                </p>

              </div>
              <Button
                onClick={handleCreate}
                disabled={actionLoading || (isMaster && (user?.credits || 0) < 1)}
                className="w-full bg-primary text-primary-foreground"
              >
                {actionLoading ? 'Criando...' : newUser.role === 'master' ? 'Criar Master' : newUser.role === 'simple' ? 'Criar Nexu Simple' : 'Criar Revendedor'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="glass-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total</p>
              <p className="font-heading font-bold text-2xl text-foreground">{stats.total || total}</p>
            </div>
            <Users className="w-5 h-5 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Ativos</p>
              <p className="font-heading font-bold text-2xl text-primary">{stats.active || 0}</p>
            </div>
            <Users className="w-5 h-5 text-primary" />
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Expirados</p>
              <p className="font-heading font-bold text-2xl text-destructive">{stats.expired || 0}</p>
            </div>
            <Calendar className="w-5 h-5 text-destructive" />
          </CardContent>
        </Card>
        {isMaster && (
          <Card className="glass-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Seus Créditos</p>
                <p className="font-heading font-bold text-2xl text-yellow-500">{user?.credits || 0}</p>
              </div>
              <Coins className="w-5 h-5 text-yellow-500" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Nenhum revendedor</h3>
            <p className="text-muted-foreground text-sm mb-4">Crie seu primeiro revendedor</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-primary-foreground">
              Criar Revendedor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map((u, index) => (
            <Card
              key={u.id}
              className={`glass-card hover-lift animate-fade-in stagger-${(index % 5) + 1}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      u.role === 'master' ? 'bg-yellow-500/15' : u.role === 'simple' ? 'bg-purple-500/15' : 'bg-primary/15'
                    }`}>
                      <span className={`font-heading font-bold uppercase ${
                        u.role === 'master' ? 'text-yellow-500' : u.role === 'simple' ? 'text-purple-500' : 'text-primary'
                      }`}>
                        {u.username[0]}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-medium text-foreground">{u.username}</p>
                        {getRoleBadge(u.role)}
                        <Badge variant="outline" className={u.active && !isExpired(u.expires_at) ? 'status-connected' : 'status-disconnected'}>
                          {!u.active ? 'Bloqueado' : isExpired(u.expires_at) ? 'Expirado' : 'Ativo'}
                        </Badge>
                        {u.had_trial && (
                          <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-400 border-purple-500/20">
                            Já testou
                          </Badge>
                        )}
                        {u.custom_renewal_price > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                            R$ {u.custom_renewal_price.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Wifi className="w-3 h-3" />
                          {u.max_connections === -1 ? '∞' : (u.max_connections + (u.extra_connections || 0))} conexões{u.extra_connections ? ` (${u.max_connections}+${u.extra_connections} extra${u.extra_connections > 1 ? 's' : ''})` : ''}
                        </span>
                        {u.contato_phone && (
                          <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />{u.contato_phone}</span>
                        )}
                        {(u.role === 'master' || u.role === 'simple') && (
                          <span className="flex items-center gap-1 text-yellow-500">
                            <Coins className="w-3 h-3" />
                            {u.credits || 0} créditos
                          </span>
                        )}
                        {u.expires_at && (
                          <span className={`flex items-center gap-1 ${isExpired(u.expires_at) ? 'text-destructive' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            {isExpired(u.expires_at) ? 'Expirou' : 'Expira'}: {new Date(u.expires_at).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {/* Creator badge for admin viewing all */}
                        {isAdmin && ownerFilter === 'all' && u.creator_username && (
                          <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">
                            Criado por: {u.creator_username}
                          </Badge>
                        )}
                        {/* Active gateway badge */}
                        {isAdmin && u.active_gateway && (
                          <Badge variant="outline" className={`text-[10px] ${
                            u.active_gateway === 'nexupay' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                            u.active_gateway === 'mercadopago' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20' :
                            u.active_gateway === 'ativopay' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                            'bg-orange-500/10 text-orange-400 border-orange-500/20'
                          }`}>
                            GW: {u.active_gateway}
                          </Badge>
                        )}
                        {/* Blocked badge */}
                        {u.blocked_by_admin && (
                          <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-400 border-red-500/20">
                            BLOQUEADO{u.block_reason ? `: ${u.block_reason}` : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 items-center">
                    {/* WhatsApp link - se tem conexão com numero */}
                    {u.whatsapp_number && (
                      <a href={`https://wa.me/${u.whatsapp_number.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`whatsapp-link-${u.id}`}
                        className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 transition-colors text-xs font-medium" title={`WhatsApp: ${u.whatsapp_number}`}>
                        <MessageCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="hidden sm:inline">{u.whatsapp_number}</span>
                      </a>
                    )}
                    {/* Block/Unblock - admin only */}
                    {isAdmin && !u.blocked_by_admin && (
                      <Button variant="outline" size="icon" onClick={() => { setBlockOpen(u); setBlockReason(''); }}
                        className="border-red-500/30 text-red-500 hover:bg-red-500/10 h-8 w-8" title="Bloquear">
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && u.blocked_by_admin && (
                      <Button variant="outline" size="icon" onClick={() => handleUnblock(u)}
                        className="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 h-8 w-8" title="Desbloquear">
                        <ShieldCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Trial Button - Only for users who never had trial */}
                    {canHaveTrial(u) && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setSelectedUser(u);
                          setTrialDialogOpen(true);
                        }}
                        className="border-purple-500/30 text-purple-500 hover:bg-purple-500/10 h-8 w-8"
                        title="Liberar Teste (24h)"
                      >
                        <Gift className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Renew Button */}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setSelectedUser(u);
                        setRenewDialogOpen(true);
                      }}
                      className="border-primary/30 text-primary hover:bg-primary/10 h-8 w-8"
                      title="Renovar (+1 mês)"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    {/* Add Credits (Admin, Master, Simple) */}
                    {(isAdmin || isMaster || isSimple) && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setSelectedUser(u);
                          setAddCreditsDialogOpen(true);
                        }}
                        className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10 h-8 w-8"
                        title="Adicionar créditos"
                      >
                        <Coins className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Slots de conexão (admin) */}
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => { setSlotUser(u); setSlotQty(1); setSlotDays(30); }}
                        className="border-cyan-500/30 text-cyan-500 hover:bg-cyan-500/10 h-8 w-8"
                        title="Conexões extras"
                      >
                        <Wifi className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Enviar cobrança */}
                    {u.contato_phone && (u.role === 'reseller' || u.role === 'master' || u.role === 'simple') && (
                      <Button variant="outline" size="icon" onClick={() => enviarCobranca(u)} disabled={cobrandoId === u.id}
                        className="border-border h-8 w-8 text-green-500 hover:bg-green-500/10" title="Enviar cobrança">
                        {cobrandoId === u.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    )}
                    {/* Edit */}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEditDialog(u)}
                      className="border-border h-8 w-8"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {/* Delete */}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setSelectedUser(u);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-destructive/20 text-destructive hover:bg-destructive/10 h-8 w-8"
                      title="Deletar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-border"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground px-3">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-border"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal 
        open={receiptModalOpen} 
        onClose={() => setReceiptModalOpen(false)} 
        receipt={currentReceipt} 
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="glass-card border-border w-[calc(100%-2rem)] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Editar Revendedor</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Atualize as configurações de {selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
            {/* Role selector - Admin and Master can change roles */}
            {(isAdmin || isMaster) && (
              <div className="space-y-2">
                <Label className="text-foreground">Tipo de Usuário</Label>
                <Select value={editData.role} onValueChange={(v) => setEditData({ ...editData, role: v })}>
                  <SelectTrigger className="bg-muted/50 border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reseller">Revendedor</SelectItem>
                    <SelectItem value="master">Master</SelectItem>
                    <SelectItem value="simple">Nexu Simple</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {editData.role === 'master' ? 'Masters podem criar e gerenciar revendedores usando créditos' : editData.role === 'simple' ? 'Nexu Simple: apenas Gestor de Clientes e App' : 'Revendedores são usuários finais do sistema'}
                </p>
              </div>
            )}
            {/* Conexões — só o ADMIN pode editar livremente (qualquer valor) */}
            {isAdmin && (
              <div className="space-y-2">
                <Label className="text-foreground">Conexões</Label>
                <Input type="number" min="0" value={editData.max_connections}
                  onChange={(e) => setEditData({ ...editData, max_connections: parseInt(e.target.value) || 0 })}
                  className="bg-muted/50 border-border text-foreground" />
                <p className="text-xs text-muted-foreground">Quantidade de conexões deste usuário. Como admin, você define qualquer valor.</p>
              </div>
            )}
            {isAdmin && (
              <div className="space-y-2">
                <Label className="text-foreground">Vencimento</Label>
                <Input type="date" value={editData.expires_at_date || ''}
                  onChange={(e) => setEditData({ ...editData, expires_at_date: e.target.value })}
                  className="bg-muted/50 border-border text-foreground" />
                <p className="text-xs text-muted-foreground">Data de vencimento da assinatura deste revendedor.</p>
              </div>
            )}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg sm:col-span-2">
              <div>
                <Label className="text-foreground">Status</Label>
                <p className="text-sm text-muted-foreground">{editData.active ? 'Ativo' : 'Bloqueado'}</p>
              </div>
              <Switch
                checked={editData.active}
                onCheckedChange={(checked) => setEditData({ ...editData, active: checked })}
              />
            </div>
            {/* Valor personalizado de renovação */}
            <div className="space-y-2">
              <Label className="text-foreground">Valor Personalizado (R$)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={editData.custom_renewal_price}
                onChange={(e) => setEditData({ ...editData, custom_renewal_price: e.target.value })}
                placeholder="Deixe vazio para usar o valor do plano"
                className="bg-muted/50 border-border text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                Sobrescreve o valor padrao do plano na renovacao deste painel. Deixe 0 ou vazio para remover.
              </p>
            </div>
            {(selectedUser?.role === 'reseller' || selectedUser?.role === 'master' || selectedUser?.role === 'simple') && minhasConexoes.length > 0 && (
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-foreground">Conexão para cobrar este revendedor</Label>
                <select value={editData.billing_connection_id || ''} onChange={(e) => setEditData({ ...editData, billing_connection_id: e.target.value || null })}
                  className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-foreground text-sm">
                  <option value="">Usar a conexão padrão</option>
                  {minhasConexoes.map((c) => <option key={c.id} value={c.id}>{c.name || c.phone_number}</option>)}
                </select>
                <p className="text-xs text-muted-foreground">Qual das SUAS conexões envia a cobrança para ele. Vazio = a padrão das configurações.</p>
              </div>
            )}

            {/* Upline - apenas admin */}
            {isAdmin && (
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-foreground">Upline (Revendedor Superior)</Label>
                <div className="flex gap-2">
                  <Input
                    value={uplineSearch}
                    onChange={(e) => setUplineSearch(e.target.value)}
                    placeholder="Username do upline"
                    className="bg-muted/50 border-border text-foreground flex-1"
                  />
                  <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={async () => {
                    if (!uplineSearch.trim()) return;
                    try {
                      const r = await api.get(`/admin/all-users?page=1&limit=5&owner_filter=all&search=${uplineSearch.trim()}`);
                      const found = r.data.users?.find(u => u.username.toLowerCase() === uplineSearch.trim().toLowerCase() && u.id !== selectedUser?.id);
                      if (found) {
                        setEditData({ ...editData, created_by_override: found.id });
                        setUplineSearch(found.username);
                        toast.success(`Upline: ${found.username} (${found.role})`);
                      } else {
                        toast.error('Usuario nao encontrado');
                      }
                    } catch { toast.error('Erro ao buscar'); }
                  }}>Buscar</Button>
                </div>
                {selectedUser?.creator_username && (
                  <p className="text-xs text-muted-foreground">Atual: <span className="text-primary">{selectedUser.creator_username}</span></p>
                )}
                {editData.created_by_override && (
                  <p className="text-xs text-emerald-400">Novo upline sera aplicado ao salvar</p>
                )}
              </div>
            )}
            <Button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="w-full bg-primary text-primary-foreground sm:col-span-2"
            >
              {actionLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trial Dialog */}
      <AlertDialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <AlertDialogContent className="glass-card border-border mx-4 max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-500" />
              Liberar Teste
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Liberar teste de 24 horas para &ldquo;{selectedUser?.username}&rdquo;? 
              <br /><br />
              <span className="text-yellow-500">⚠️ O usuário só pode ter 1 teste. Após isso, precisará renovar.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTrial}
              className="bg-purple-500 text-white hover:bg-purple-600"
            >
              {actionLoading ? 'Liberando...' : 'Liberar Teste (24h)'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renew Dialog */}
      <AlertDialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <AlertDialogContent className="glass-card border-border mx-4 max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Renovar Revendedor</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {isMaster 
                ? `Renovar "${selectedUser?.username}" por +1 mês custará 1 crédito. Você tem ${user?.credits || 0} créditos.`
                : `Renovar "${selectedUser?.username}" por mais 1 mês? (créditos ilimitados)`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedUser?.contato_phone && (
            <label className="flex items-center justify-between rounded-lg bg-muted/40 p-3 my-2 cursor-pointer">
              <span className="text-sm text-foreground">📲 Notificar no WhatsApp</span>
              <Switch checked={renewNotify} onCheckedChange={setRenewNotify} />
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRenew}
              disabled={isMaster && (user?.credits || 0) < 1}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {actionLoading ? 'Renovando...' : 'Renovar (+1 mês)'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Credits Dialog */}
      <Dialog open={addCreditsDialogOpen} onOpenChange={setAddCreditsDialogOpen}>
        <DialogContent className="glass-card border-border mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Transferir Créditos</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Transferir créditos para <strong>{selectedUser?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Saldos atuais */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Você</p>
                <p className="text-lg font-bold text-yellow-500">{isAdmin ? '∞' : (user?.credits || 0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{selectedUser?.username}</p>
                <p className="text-lg font-bold text-blue-500">{selectedUser?.credits || 0}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Quantidade</Label>
              <Input
                type="number"
                min={1}
                max={isAdmin ? 999999 : (user?.credits || 0)}
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(Math.max(1, parseInt(e.target.value) || 1))}
                className="bg-muted/50 border-border text-foreground"
              />
              {!isAdmin && creditsToAdd > (user?.credits || 0) && (
                <p className="text-xs text-red-400">Você não tem créditos suficientes</p>
              )}
            </div>

            {/* Resultado após transferência */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Você após</p>
                <p className="text-lg font-bold text-red-400">{isAdmin ? '∞' : Math.max(0, (user?.credits || 0) - creditsToAdd)}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{selectedUser?.username} após</p>
                <p className="text-lg font-bold text-green-400">{(selectedUser?.credits || 0) + creditsToAdd}</p>
              </div>
            </div>

            <Button
              onClick={handleAddCredits}
              disabled={actionLoading || (!isAdmin && creditsToAdd > (user?.credits || 0))}
              className="w-full bg-yellow-500 text-yellow-950 hover:bg-yellow-400"
            >
              {actionLoading ? 'Transferindo...' : `Transferir ${creditsToAdd} Crédito(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="glass-card border-border mx-4 max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Deletar Revendedor</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Tem certeza que deseja deletar &ldquo;{selectedUser?.username}&rdquo;? Todas as conexões e campanhas serão removidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? 'Deletando...' : 'Deletar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="glass-card border-border mx-4 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-foreground">
              {bulkDeleteType === 'resellers' ? (
                <AlertTriangle className="w-5 h-5 text-destructive" />
              ) : (
                <TestTube className="w-5 h-5 text-orange-500" />
              )}
              {bulkDeleteType === 'resellers' ? 'Apagar Revendedores Expirados' : 'Apagar Testes Expirados'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {bulkDeleteType === 'resellers' ? (
                <>
                  Esta ação irá deletar <strong>{stats.expired}</strong> revendedores expirados e todas as suas conexões, campanhas, clientes e instâncias WhatsApp.
                  <br /><br />
                  <span className="text-destructive">Esta ação não pode ser desfeita!</span>
                </>
              ) : (
                <>
                  Esta ação irá deletar todos os usuários de teste expirados e suas instâncias WhatsApp.
                  <br /><br />
                  <span className="text-orange-500">Esta ação não pode ser desfeita!</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={actionLoading}
              className={bulkDeleteType === 'resellers' 
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-orange-500 text-white hover:bg-orange-600"
              }
            >
              {actionLoading ? 'Deletando...' : 'Confirmar Exclusão'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block Dialog */}
      <Dialog open={!!blockOpen} onOpenChange={() => setBlockOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-400">Bloquear {blockOpen?.username}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Isso vai bloquear este usuario e <strong>todos abaixo dele</strong>. Conexoes WhatsApp serao desconectadas e clientes de app nao conseguirao logar.</p>
            <div>
              <Label className="text-xs">Motivo (opcional)</Label>
              <textarea value={blockReason} onChange={e => setBlockReason(e.target.value)} rows={2} placeholder="Ex: Inadimplencia, Uso indevido..." className="w-full text-sm p-2 rounded-md border bg-background mt-1 resize-none" />
            </div>
            <Button onClick={handleBlock} disabled={blocking} variant="destructive" className="w-full">
              {blocking ? 'Bloqueando...' : 'Confirmar Bloqueio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: admin adiciona slots de conexão */}
      {slotUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSlotUser(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card border border-border p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Wifi className="w-5 h-5 text-cyan-500" />
              <h3 className="font-semibold text-foreground">Conexões extras</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Adicionar ou remover conexões extras para <b className="text-foreground">{slotUser.username}</b>, sem cobrança.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-muted-foreground">Quantidade</label>
                <input type="number" value={slotQty} onChange={e => setSlotQty(parseInt(e.target.value) || 0)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-foreground" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Validade (dias)</label>
                <input type="number" value={slotDays} onChange={e => setSlotDays(parseInt(e.target.value) || 30)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-foreground" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" disabled={slotSaving} onClick={() => salvarSlots(-Math.abs(slotQty))}>− Remover</Button>
              <Button className="flex-1" disabled={slotSaving} onClick={() => salvarSlots(Math.abs(slotQty))}>+ Adicionar</Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COBRANÇA/AVISOS DOS REVENDEDORES */}
      <Dialog open={cobrancaOpen} onOpenChange={setCobrancaOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Cobrança e avisos dos revendedores</DialogTitle>
          </DialogHeader>
          {!cobrancaCfg ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Carregando...</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
                <div><p className="text-sm font-medium">Enviar cobrança automática</p><p className="text-xs text-muted-foreground">Avisa os revendedores que estão vencendo (3/2/1/0 dias).</p></div>
                <Switch checked={cobrancaCfg.billing_ativo !== false} onCheckedChange={(v) => setCobrancaCfg({ ...cobrancaCfg, billing_ativo: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Horário de envio</Label>
                  <Input type="time" value={cobrancaCfg.billing_hora || '09:00'} onChange={(e) => setCobrancaCfg({ ...cobrancaCfg, billing_hora: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Delay entre envios (segundos)</Label>
                  <Input type="number" min="0" value={cobrancaCfg.billing_delay_seconds || 0} onChange={(e) => setCobrancaCfg({ ...cobrancaCfg, billing_delay_seconds: parseInt(e.target.value) || 0 })} className="mt-1" />
                </div>
              </div>
              {cobrancaCfg.conexoes?.length > 0 && (
                <div>
                  <Label className="text-xs">Conexão que envia a cobrança e a renovação</Label>
                  <select value={cobrancaCfg.connection_id || ''} onChange={(e) => setCobrancaCfg({ ...cobrancaCfg, connection_id: e.target.value || null })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm">
                    <option value="">Principal (1ª conexão)</option>
                    {cobrancaCfg.conexoes.map((c) => {
                      const on = c.status?.connected || c.status === 'connected';
                      return <option key={c.id} value={c.id}>{on ? '🟢 ' : '🔴 '}{c.name || c.phone_number || 'Conexão'}</option>;
                    })}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">Escolha qual número envia as mensagens aos revendedores. 🟢 = conectada.</p>
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
                <span className="text-sm">Também enviar no WhatsApp</span>
                <Switch checked={cobrancaCfg.enviar_whatsapp !== false} onCheckedChange={(v) => setCobrancaCfg({ ...cobrancaCfg, enviar_whatsapp: v })} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
                <div>
                  <p className="text-sm">Botão "💠 Renovar" nas cobranças</p>
                  <p className="text-[11px] text-muted-foreground">Adiciona um botão clicável que abre a renovação.</p>
                </div>
                <Switch checked={cobrancaCfg.billing_button_enabled !== false} onCheckedChange={(v) => setCobrancaCfg({ ...cobrancaCfg, billing_button_enabled: v })} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3">
                <div>
                  <p className="text-sm">📄 Enviar comprovante (PDF)</p>
                  <p className="text-[11px] text-muted-foreground">Após a renovação, envia o comprovante em PDF numa segunda mensagem. Use {'{comprovante}'} nas mensagens.</p>
                </div>
                <Switch checked={!!cobrancaCfg.enviar_comprovante} onCheckedChange={(v) => setCobrancaCfg({ ...cobrancaCfg, enviar_comprovante: v })} />
              </div>
              <div className="pt-2 border-t border-border/50 space-y-2">
                <p className="text-xs font-medium text-foreground">Mensagens</p>
                <p className="text-[11px] text-muted-foreground">Clique para editar cada mensagem.</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { k: 'msg_renovacao_confirmada', l: 'Renovação confirmada', ic: '✅' },
                    { k: 'msg_aviso_3', l: '3 dias antes', ic: '⏳' },
                    { k: 'msg_aviso_2', l: '2 dias antes', ic: '⏳' },
                    { k: 'msg_aviso_1', l: '1 dia antes', ic: '⚠️' },
                    { k: 'msg_aviso_0', l: 'No dia', ic: '🚨' },
                    { k: 'msg_aviso_pos', l: 'Após vencer (1 dia)', ic: '❌' },
                  ].map(({ k, l, ic }) => {
                    const temImg = !!cobrancaCfg[k.replace('msg_', 'img_')];
                    return (
                      <button key={k} onClick={() => setMsgEdit({ k, l })}
                        className="flex items-center gap-2 p-3 rounded-xl border border-border/60 hover:border-primary/50 bg-muted/20 transition-colors text-left">
                        <span className="text-lg">{ic}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{l}</p>
                          <p className="text-[10px] text-muted-foreground">{temImg ? '🖼️ com imagem' : 'editar'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button onClick={salvarCobrancaConfig} disabled={cobrancaSaving} className="w-full">
                {cobrancaSaving ? 'Salvando...' : 'Salvar configuração'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SUB-MODAL: editar uma mensagem */}
      <Dialog open={!!msgEdit} onOpenChange={(v) => !v && setMsgEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{msgEdit?.l}</DialogTitle>
          </DialogHeader>
          {msgEdit && cobrancaCfg && (
            <div className="space-y-3">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1.5">Toque para inserir uma variável:</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { v: '{nome}', d: 'nome do revendedor' },
                    { v: '{vencimento}', d: 'data de vencimento' },
                    { v: '{dias_restantes}', d: 'dias até vencer' },
                    { v: '{link}', d: 'link para renovar' },
                    ...(msgEdit.k === 'msg_renovacao_confirmada' ? [{ v: '{comprovante}', d: 'link do comprovante PDF' }] : []),
                  ].map(({ v, d }) => (
                    <button key={v} type="button" title={d}
                      onClick={() => setCobrancaCfg({ ...cobrancaCfg, [msgEdit.k]: (cobrancaCfg[msgEdit.k] || '') + v })}
                      className="text-[11px] font-mono px-2 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={cobrancaCfg[msgEdit.k] || ''} onChange={(e) => setCobrancaCfg({ ...cobrancaCfg, [msgEdit.k]: e.target.value })}
                rows={6} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm resize-none" autoFocus />
              <div>
                <Label className="text-xs">Imagem (opcional)</Label>
                <div className="flex items-center gap-2 mt-1">
                  {cobrancaCfg[msgEdit.k.replace('msg_', 'img_')] ? (
                    <>
                      <img src={cobrancaCfg[msgEdit.k.replace('msg_', 'img_')]} alt="" className="h-14 w-14 object-cover rounded-lg" />
                      <button onClick={() => setCobrancaCfg({ ...cobrancaCfg, [msgEdit.k.replace('msg_', 'img_')]: null })} className="text-xs text-red-500">remover</button>
                    </>
                  ) : (
                    <label className="text-xs text-primary cursor-pointer hover:underline flex items-center gap-1">
                      <Plus className="w-3 h-3" /> adicionar imagem
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCobrancaImg(msgEdit.k, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setMsgEdit(null)}>Cancelar</Button>
                <Button className="flex-1" onClick={() => setMsgEdit(null)}>OK</Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">As alterações são salvas ao clicar em "Salvar configuração".</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
